import axios from "axios";
import { acquireToken } from "../authentication/utils/authUtils";

const toMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.errorMessage ||
    error?.message ||
    String(error)
  );
};

const getHttpDetail = (response) => {
  if (!response) {
    return null;
  }

  return `${response.status} ${response.statusText || ""}`.trim();
};

export const getAuthState = ({ instance, account }) => {
  const allAccounts = instance?.getAllAccounts?.() || [];
  const activeAccount = account || instance?.getActiveAccount?.() || allAccounts[0] || null;
  const roles = activeAccount?.idTokenClaims?.roles || [];

  if (!activeAccount) {
    return {
      key: "msal-account",
      label: "Microsoft account",
      status: "fail",
      message: "No active Microsoft account found",
      detail: `Cached accounts: ${allAccounts.length}`,
    };
  }

  return {
    key: "msal-account",
    label: "Microsoft account",
    status: "pass",
    message: `Signed in as ${activeAccount.username || activeAccount.name || "unknown user"}`,
    detail: roles.length ? `Roles: ${roles.join(", ")}` : "No app roles found in token claims",
  };
};

export const getBrowserCapabilities = async () => {
  const checks = [];

  checks.push({
    key: "browser-secure-context",
    label: "Secure context",
    status: window.isSecureContext ? "pass" : "fail",
    message: window.isSecureContext
      ? "Tools is running in a secure browser context"
      : "Browser APIs for calling require HTTPS / secure context",
    detail: window.location.origin,
  });

  checks.push({
    key: "browser-webrtc",
    label: "WebRTC support",
    status: window.RTCPeerConnection ? "pass" : "fail",
    message: window.RTCPeerConnection
      ? "RTCPeerConnection is available"
      : "RTCPeerConnection is not available in this browser",
  });

  checks.push({
    key: "browser-media",
    label: "Microphone API",
    status: navigator.mediaDevices?.getUserMedia ? "pass" : "fail",
    message: navigator.mediaDevices?.getUserMedia
      ? "getUserMedia is available"
      : "Microphone capture API is not available",
  });

  checks.push({
    key: "browser-websocket",
    label: "WebSocket support",
    status: window.WebSocket ? "pass" : "fail",
    message: window.WebSocket
      ? "WebSocket is available"
      : "WebSocket is not available in this browser",
  });

  try {
    if (navigator.permissions?.query) {
      const permission = await navigator.permissions.query({ name: "microphone" });
      checks.push({
        key: "browser-microphone-permission",
        label: "Microphone permission",
        status: permission.state === "denied" ? "fail" : "pass",
        message: `Microphone permission is ${permission.state}`,
        detail: "Browsers may still ask again when starting a call.",
      });
    } else {
      checks.push({
        key: "browser-microphone-permission",
        label: "Microphone permission",
        status: "warn",
        message: "Browser permission query API is unavailable",
        detail: "This is common in some browsers; calling may still work after a prompt.",
      });
    }
  } catch (permissionError) {
    checks.push({
      key: "browser-microphone-permission",
      label: "Microphone permission",
      status: "warn",
      message: "Unable to query microphone permission",
      detail: toMessage(permissionError),
    });
  }

  return checks;
};

export const checkBackendHealth = async () => {
  try {
    const response = await axios.get("/api/health", { timeout: 8000 });
    const ok = Boolean(response?.data?.ok);

    return {
      key: "backend-health",
      label: "Backend health",
      status: ok ? "pass" : "warn",
      message: ok ? `${response.data.service || "backend2-api"} is healthy` : "Backend responded without ok=true",
      detail: getHttpDetail(response),
    };
  } catch (error) {
    return {
      key: "backend-health",
      label: "Backend health",
      status: "fail",
      message: "Unable to reach backend health endpoint",
      detail: toMessage(error),
    };
  }
};

export const checkPhoneConfig = async ({ accessToken }) => {
  try {
    const response = await axios.get("/api/phone/config", {
      timeout: 8000,
      headers: { Authorization: accessToken },
    });

    const config = response?.data || {};
    const requiredFieldsPresent = Boolean(
      config.sipDomain && config.wssServer && config.extension
    );

    return {
      key: "phone-config",
      label: "Phone config",
      status: requiredFieldsPresent ? "pass" : "warn",
      message: requiredFieldsPresent
        ? `Phone config loaded for extension ${config.extension}`
        : "Phone config response is missing expected fields",
      detail: `sipDomain=${config.sipDomain || "missing"}; wssServer=${config.wssServer || "missing"}`,
    };
  } catch (error) {
    return {
      key: "phone-config",
      label: "Phone config",
      status: "fail",
      message: "Unable to load phone config",
      detail: toMessage(error),
    };
  }
};

export const runDiagnostics = async ({ instance, account }) => {
  const results = [];
  const activeAccount = account || instance?.getActiveAccount?.() || instance?.getAllAccounts?.()?.[0] || null;

  results.push(getAuthState({ instance, account: activeAccount }));
  results.push(...(await getBrowserCapabilities()));
  results.push(await checkBackendHealth());

  let accessToken = null;
  if (activeAccount) {
    try {
      const token = await acquireToken(instance, activeAccount);
      accessToken = token.accessToken;
      results.push({
        key: "msal-token",
        label: "Microsoft token",
        status: "pass",
        message: "Token acquired for Tools API",
        detail: token.expiresOn ? `Expires: ${token.expiresOn.toLocaleString()}` : null,
      });
    } catch (error) {
      results.push({
        key: "msal-token",
        label: "Microsoft token",
        status: "fail",
        message: "Unable to acquire Microsoft token",
        detail: toMessage(error),
      });
    }
  }

  if (accessToken) {
    const phoneConfigResult = await checkPhoneConfig({ accessToken });
    results.push(phoneConfigResult);
    results.push({
      key: "reminder-readiness",
      label: "Reminder readiness",
      status: phoneConfigResult.status === "pass" ? "pass" : "warn",
      message:
        phoneConfigResult.status === "pass"
          ? "Reminder prerequisites are reachable"
          : "Reminder readiness could not be fully confirmed",
      detail: "This check does not place a call or send an SMS.",
    });
  } else {
    results.push({
      key: "phone-config",
      label: "Phone config",
      status: "fail",
      message: "Skipped because Microsoft token acquisition failed",
    });
    results.push({
      key: "reminder-readiness",
      label: "Reminder readiness",
      status: "fail",
      message: "Skipped because Microsoft token acquisition failed",
    });
  }

  return results;
};
